var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback, useRef, useEffect, useContext, useMemo, memo, forwardRef, useImperativeHandle } from 'react';
import { cloneDeep } from 'lodash-es';
import classNames from 'classnames';
import BaseTableComponent, { cleanSort, processColumns } from '../Common/BaseTableComponent';
import { buildCardColumn, RowDetailsSheet, rowActionsOf } from '../Common/cardColumn';
import PaginationComponent from '../../Pagination/PaginationComponent';
import PaginationButton from '../../Pagination/PaginationButton';
import useToast from '@notifications/Toast/useToast';
import { ViewSkeleton } from '@widget/loaders';
import SearchBox from '@widget/forms/inputs/SearchBox';
import useDebounce from '@hooks/useDebounce';
import Checkbox from '@widget/forms/inputs/Checkbox';
import Badge from '@widget/other/Badge';
import Input from '@widget/forms/inputs/Input';
import Button from '@widget/buttons/Button';
import useAlert from '@notifications/Alerts/useAlerts';
import NotificationBlock from '@helper/NotificationBlock';
import { ObjectDetailsPanel } from './ObjectDetailsPanel';
import { ViewsList } from './ViewsList';
import { getPredefinedQueries, getPredefinedQueryPipeline, resolvePredefinedQueryById, findQueryByName, createPredefinedQuery, saveChangesToQuery } from './predefined-queries-utils';
import { cleanFilters, FilterView, getFilterCount } from './FilterView';
import { PipelineRefineModal } from './PipelineRefineModal';
import { UXPContext } from '@core/context/Context';
import { hasValue, simpleHash, textSearch, toNum } from '@utils';
import { useExportData } from '@hooks/useExportData';
import { useRouterContext, useSearchParams } from '@hooks/useRouteHandlers';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import { SlideInForm } from '@widget/forms/DataEntryForm';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
import MobileToolbar from '@widget/other/MobileToolbar';
import MobileSheet from '@widget/popups/MobileSheet';
import IconButton from '@widget/buttons/IconButton';
import SummaryStrip from './SummaryStrip';
import ButtonListContext from '@widget/buttons/ButtonListContext';
import { useSectionBarHost } from '@widget/forms/ConfigurationView/ConfigSectionBar';
import useMobileLayer from '@core/layout/MobileLayer';
import ToggleFilter from '@widget/forms/inputs/ToggleFilter';
import CardGrid, { pickCardField } from './CardGrid';
import ListItemCard from '../../ListItemCard';
import useInfinitePaging from '@hooks/useInfinitePaging';
import { sortRows } from '../Common/sortRows';
import InfiniteFooter from '../Common/InfiniteFooter';
function hashPipeline(pipeline) {
    var _a;
    return (_a = simpleHash(JSON.stringify(pipeline), '', { base: 36, ignorePrefix: true })) !== null && _a !== void 0 ? _a : '';
}
/**
 * Search box that keeps the typed value in local state and only lifts a debounced
 * value to the parent. This prevents every keystroke from re-rendering the whole
 * ObjectSearchComponent tree (which is expensive when the details panel is open).
 */
const DebouncedSearchBox = ({ onChange, collapsed, delay = 300 }) => {
    const [local, setLocal] = useState('');
    const debounced = useDebounce(local, delay);
    const isFirst = useRef(true);
    useEffect(() => {
        if (isFirst.current) {
            isFirst.current = false;
            return;
        }
        onChange(debounced);
    }, [debounced, onChange]);
    return (_jsx(SearchBox, { value: local, onChange: setLocal, collapsed: collapsed || false, position: 'left' }));
};
const ObjectSearchComponentBase = forwardRef((_a, ref) => {
    var _b, _c, _d, _e, _f, _g, _h;
    var { data: propData, columns: propColumns, defaultActionColumns, pageSize: initialPageSize, total: propTotal, idField, nameField, typeField, onSelect, selected = [], minCellWidth = 100, detailsPanel, cardColumn, onClickRow, views, filters: filterConfig, collapsedWidth: propCollapsedWidth, appendToURL = true, clearParamsOnSelect, urlParams: propUrlParams, onFilterPanelToggle, display: propDisplay, displayModes, paging, card, renderCard } = _a, props = __rest(_a, ["data", "columns", "defaultActionColumns", "pageSize", "total", "idField", "nameField", "typeField", "onSelect", "selected", "minCellWidth", "detailsPanel", "cardColumn", "onClickRow", "views", "filters", "collapsedWidth", "appendToURL", "clearParamsOnSelect", "urlParams", "onFilterPanelToggle", "display", "displayModes", "paging", "card", "renderCard"]);
    const context = useContext(UXPContext);
    const toast = useToast();
    const alerts = useAlert();
    const { navigate, generateURL } = useRouterContext();
    // Merge URL params with defaults.
    // Default changed from 'key' to 'objectKey' — 'key' is a React reserved prop and
    // must not flow through as additionalProps to createElement (causes widget remounts).
    const urlParams = useMemo(() => ({
        key: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.key) || 'objectKey',
        detailsTab: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.detailsTab) || 'dt',
        additionalDetailsTab: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.additionalDetailsTab) || 'ad',
        name: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.name) || 'objectID',
        type: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.type) || 'objectType',
    }), [propUrlParams]);
    const searchParams = useSearchParams();
    // Backward compat: fall back to the legacy 'key' param when using the default 'objectKey'.
    // This ensures old bookmarked URLs (?key=123) still open the details panel correctly.
    const key = searchParams[urlParams.key] || (urlParams.key === 'objectKey' ? searchParams['key'] : null);
    const searchConfigRef = useRef(props === null || props === void 0 ? void 0 : props.search);
    searchConfigRef.current = props === null || props === void 0 ? void 0 : props.search;
    const containerRef = useRef(null);
    const listContainerRef = useRef(null);
    const hasFiltersChangedRef = useRef(false);
    const hasSortChangedRef = useRef(false);
    const hasColumnsChangedRef = useRef(false);
    const actionsDropdwonRef = useRef(null);
    const sortDropdwonRef = useRef(null);
    const columnsDropdwonRef = useRef(null);
    const viewsListRef = useRef(null);
    const saveQueryFormRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [listContainerWidth, setListContainerWidth] = useState(0);
    const [allColumns, setAllColumns] = useState([]);
    const [processedColumns, setProcessedColumns] = useState(null);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(initialPageSize || 10);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [filters, setFilters] = useState(null);
    const [sort, setSort] = useState({});
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    useEffect(() => {
        onFilterPanelToggle === null || onFilterPanelToggle === void 0 ? void 0 : onFilterPanelToggle(showFilterPanel);
    }, [showFilterPanel, onFilterPanelToggle]);
    const [showViewsList, setShowViewsList] = useState(false);
    const [resizedColumns, setResizedColumns] = useState({});
    const [viewInstance, setViewInstance] = useState(null);
    const [viewAction, setViewAction] = useState(null);
    const [viewName, setViewName] = useState('');
    const [selectedView, setSelectedView] = useState(null);
    // Card mode and infinite paging (both off unless the page asks for them)
    const defaultDisplay = propDisplay || 'list';
    const [displayMode, setDisplayMode] = useState(defaultDisplay);
    const isCards = displayMode === 'cards';
    const canToggleDisplay = ((displayModes === null || displayModes === void 0 ? void 0 : displayModes.length) || 0) > 1;
    const isInfinite = paging === 'infinite';
    // Details panel open: card mode keeps a narrow list of compact cards (25%), lists half (50%)
    // Details panel open: a narrow list (25%) beside the details, for lists and cards alike
    const collapsedWidth = propCollapsedWidth !== null && propCollapsedWidth !== void 0 ? propCollapsedWidth : '25%';
    const onFirstPageError = useCallback(() => toast.error('Unable to get data. Something went wrong.'), [toast]);
    const infinitePaging = useInfinitePaging({ pageSize, idField, setData, setLoading, onFirstPageError });
    const { appending, loadError } = infinitePaging;
    // A silent reload from the top (refresh on a later page) keeps its silent flag through setPage(1)
    const silentReloadRef = useRef(false);
    // Advanced filter state
    const [filterMode, setFilterMode] = useState('simple');
    const [advancedQuestion, setAdvancedQuestion] = useState('');
    const [advancedPipeline, setAdvancedPipeline] = useState(null);
    const [advancedData, setAdvancedData] = useState(null);
    const [extraColumns, setExtraColumns] = useState([]);
    const [advancedLoading, setAdvancedLoading] = useState(false);
    const [advancedError, setAdvancedError] = useState(null);
    const [originalPipeline, setOriginalPipeline] = useState(null);
    const [originalPipelineHash, setOriginalPipelineHash] = useState(null);
    const [pipelineHash, setPipelineHash] = useState(null);
    const [showRefineModal, setShowRefineModal] = useState(false);
    const [pipelineRefined, setPipelineRefined] = useState(false);
    // Predefined queries (authored in Lucy Data Explorer) for filterConfig.advanced.objectType.
    const [predefinedQueries, setPredefinedQueries] = useState([]);
    const [predefinedLoading, setPredefinedLoading] = useState(false);
    // Marks the active pipeline as originating from a predefined query (rides saved views).
    const [predefinedQuery, setPredefinedQuery] = useState(null);
    // Open state for the "Save as query" form (create a new predefined query from the current pipeline).
    const [showSaveQueryForm, setShowSaveQueryForm] = useState(false);
    // The (edited) pipeline + schema captured from the refine editor to save as a new query.
    const [saveQueryPipeline, setSaveQueryPipeline] = useState(null);
    const [saveQuerySchema, setSaveQuerySchema] = useState(null);
    // Set when a saved view references a predefined query that no longer exists.
    const [predefinedUnavailable, setPredefinedUnavailable] = useState(null);
    // Set when a predefined query's output fields don't cover the view's columns
    // ('none' → not usable, 'partial' → some columns missing).
    const [fieldValidation, setFieldValidation] = useState(null);
    // `query` is fed a debounced value by DebouncedSearchBox (typing stays in that
    // child's local state), so no second debounce is needed here.
    const [debouncedFilters, , immediateUpdateFilters] = useDebounce(filters, 300, true);
    const [debouncedSort, , immediateUpdateSort] = useDebounce(sort, 300, true);
    const isDetailsPanelShowing = useMemo(() => !!detailsPanel && hasValue(viewInstance), [detailsPanel, viewInstance]);
    // Phones always list the compact column set the details panel uses (detailsPanel.columns / columnsToHide)
    const isMobile = useIsMobileLayout();
    const useCompactColumns = isDetailsPanelShowing || isMobile;
    // Phones: views for the toolbar slider (ViewsList loads them), More and bulk-action sheets
    const [loadedViews, setLoadedViews] = useState({});
    const [moreOpen, setMoreOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const moreListMode = useMemo(() => ({ onAction: () => setMoreOpen(false) }), []);
    // Phones, inside an open configuration section: its buttons go at the end of this toolbar
    const sectionHost = useSectionBarHost();
    const bulkListMode = useMemo(() => ({ onAction: () => setBulkOpen(false) }), []);
    const handleSearchChange = useCallback((value) => {
        setQuery(value);
        setPage(1);
    }, []);
    const { exportData } = useExportData();
    const hasViews = useMemo(() => {
        var _a;
        return !!views && (hasValue(views.listId) || !!((_a = views.defaultViews) === null || _a === void 0 ? void 0 : _a.length));
    }, [views]);
    const hasDefaultViews = useMemo(() => {
        var _a;
        return hasViews && !!((_a = views.defaultViews) === null || _a === void 0 ? void 0 : _a.length);
    }, [views, hasViews]);
    const hasViewSelected = !hasDefaultViews || (hasDefaultViews && !!selectedView);
    const exportCurrentView = useCallback(() => {
        const exportColumns = Object.values(processedColumns).filter(c => !c.id.startsWith('__')).map(c => ({
            id: c.id,
            label: typeof c.label == 'string' ? c.label : (c === null || c === void 0 ? void 0 : c.exportLabel) || c.id,
            formatter: c.formatter || null
        }));
        return exportData({
            data: propData,
            total: total,
            columns: exportColumns,
            query,
            filters: debouncedFilters,
            sort: debouncedSort,
            // larger pages = fewer round-trips through the export pipe
            pageSize: 500,
            fileName: (selectedView === null || selectedView === void 0 ? void 0 : selectedView.name) || (typeof (props === null || props === void 0 ? void 0 : props.title) == 'string' && hasValue(props === null || props === void 0 ? void 0 : props.title) ? props.title : 'Export')
        });
    }, [exportData, propData, debouncedFilters, query, debouncedSort, total, selectedView, props === null || props === void 0 ? void 0 : props.title]);
    const discoverExtraColumns = useCallback((rows) => {
        if (!rows || rows.length === 0)
            return [];
        const existingIds = new Set(propColumns.map(c => c.id));
        return Object.keys(rows[0])
            .filter(k => !k.startsWith('__') && !existingIds.has(k))
            .map(k => ({ id: k, label: k }));
    }, [propColumns]);
    // Validate a pipeline's output field names against the columns this view will display.
    // Returns null when everything's covered (or there's nothing to check); otherwise flags
    // 'none' (no column present) or 'partial' (some columns missing) with the missing ids.
    const validateOutputFields = useCallback((fields) => {
        const required = ((selectedColumns === null || selectedColumns === void 0 ? void 0 : selectedColumns.length) ? selectedColumns : propColumns.map(c => c.id))
            .filter(id => id && !id.startsWith('__'));
        if (required.length === 0 || fields.length === 0)
            return null; // nothing to check
        const out = new Set(fields);
        const missing = required.filter(id => !out.has(id));
        if (missing.length === required.length)
            return { status: 'none', missing };
        if (missing.length > 0)
            return { status: 'partial', missing };
        return null;
    }, [selectedColumns, propColumns]);
    const runAdvancedSearch = useCallback((question) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!((_a = filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.advanced) === null || _a === void 0 ? void 0 : _a.baseDataSource) || !question.trim())
            return;
        setAdvancedLoading(true);
        setAdvancedError(null);
        try {
            const qe = (_c = (_b = window.QueryBuilderComponents) === null || _b === void 0 ? void 0 : _b.useQueryEngineService) === null || _c === void 0 ? void 0 : _c.call(_b, context);
            if (!qe) {
                setAdvancedError(context.$L('uxp-core.text.query-engine-unavailable'));
                return;
            }
            const columnIds = propColumns.map(c => c.id);
            const requiredFields = columnIds.includes(idField) ? columnIds : [...columnIds, idField];
            const result = yield qe.aiSearch({
                question,
                baseDataSource: filterConfig.advanced.baseDataSource,
                requiredFields,
                execute: true,
            });
            const pipeline = result === null || result === void 0 ? void 0 : result.pipeline;
            // Invalid pipeline — engine returned a clarification question instead of sources/steps
            if (!(pipeline === null || pipeline === void 0 ? void 0 : pipeline.sources) || !(pipeline === null || pipeline === void 0 ? void 0 : pipeline.steps)) {
                setAdvancedError(context.$L('uxp-core.text.advanced-filter-no-results'));
                return;
            }
            const rows = (result === null || result === void 0 ? void 0 : result.result) || [];
            const hash = hashPipeline(pipeline);
            setAdvancedPipeline(pipeline);
            setOriginalPipeline(pipeline);
            setAdvancedData(rows);
            setExtraColumns(discoverExtraColumns(rows));
            setPipelineHash(hash);
            setOriginalPipelineHash(hash);
            setPipelineRefined(false);
            setPredefinedQuery(null); // a fresh AI question is not a predefined query
            setPredefinedUnavailable(null);
            setFieldValidation(null);
            setPage(1);
            hasFiltersChangedRef.current = true;
        }
        catch (e) {
            setAdvancedError(context.$L('uxp-core.text.advanced-filter-no-results'));
            console.error('Advanced search error:', e);
        }
        finally {
            setAdvancedLoading(false);
        }
    }), [context, propColumns, filterConfig, discoverExtraColumns]);
    const runSavedPipeline = useCallback((pipeline) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!pipeline)
            return;
        setAdvancedLoading(true);
        try {
            const qe = (_b = (_a = window.QueryBuilderComponents) === null || _a === void 0 ? void 0 : _a.useQueryEngineService) === null || _b === void 0 ? void 0 : _b.call(_a, context);
            if (!qe) {
                toast.error('Query Engine service is not available.');
                return;
            }
            const execResult = yield qe.executeQuery(pipeline);
            const rows = ((_c = execResult === null || execResult === void 0 ? void 0 : execResult.data) === null || _c === void 0 ? void 0 : _c.result) || [];
            setAdvancedData(rows);
            setExtraColumns(discoverExtraColumns(rows));
            setFieldValidation(rows.length ? validateOutputFields(Object.keys(rows[0])) : null);
            setPage(1);
        }
        catch (e) {
            toast.error('Failed to load saved query. Please try again.');
            console.error('Run saved pipeline error:', e);
        }
        finally {
            setAdvancedLoading(false);
        }
    }), [context, discoverExtraColumns, validateOutputFields]);
    const handleRefinementApplied = useCallback((newPipeline) => {
        const newHash = hashPipeline(newPipeline);
        const isChanged = newHash !== originalPipelineHash;
        if (isChanged) {
            setAdvancedPipeline(newPipeline);
            setPipelineHash(newHash);
            runSavedPipeline(newPipeline);
            setPipelineRefined(true);
            hasFiltersChangedRef.current = true;
        }
        setShowRefineModal(false);
    }, [originalPipelineHash, runSavedPipeline]);
    const handleRestoreOriginal = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!originalPipeline)
            return;
        const confirmed = yield alerts.confirm({
            title: context.$L('uxp-core.text.restore-original-pipeline-confirm-title'),
            content: context.$L('uxp-core.text.restore-original-pipeline-confirm-content'),
        });
        if (!confirmed)
            return;
        setAdvancedPipeline(originalPipeline);
        setPipelineHash(originalPipelineHash);
        setPipelineRefined(false);
        runSavedPipeline(originalPipeline);
        hasFiltersChangedRef.current = true;
    }), [originalPipeline, originalPipelineHash, runSavedPipeline, context]);
    // Load the predefined-query list for the configured object type (advanced mode only).
    const objectType = (_b = filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.advanced) === null || _b === void 0 ? void 0 : _b.objectType;
    const refreshPredefinedQueries = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!objectType)
            return;
        setPredefinedLoading(true);
        try {
            setPredefinedQueries(yield getPredefinedQueries(context, objectType));
        }
        finally {
            setPredefinedLoading(false);
        }
    }), [context, objectType]);
    useEffect(() => {
        // Phones load them up front: the filters sheet offers Advanced only when there are queries to pick
        if ((filterMode !== 'advanced' && !isMobile) || !objectType)
            return;
        refreshPredefinedQueries();
    }, [filterMode, objectType, refreshPredefinedQueries, isMobile]);
    // Pick a predefined query: load its pipeline as the new original, then execute it.
    // Editing it afterwards flips pipelineRefined (existing hash diff) → the "modified"
    // banner + Restore, exactly like an AI-generated pipeline.
    const handlePickPredefined = useCallback((q) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        setAdvancedLoading(true);
        setAdvancedError(null);
        try {
            const pipeline = yield getPredefinedQueryPipeline(context, q.Key);
            if (!(pipeline === null || pipeline === void 0 ? void 0 : pipeline.sources) || !(pipeline === null || pipeline === void 0 ? void 0 : pipeline.steps)) {
                setAdvancedError(context.$L('uxp-core.text.advanced-filter-no-results'));
                return;
            }
            const qe = (_b = (_a = window.QueryBuilderComponents) === null || _a === void 0 ? void 0 : _a.useQueryEngineService) === null || _b === void 0 ? void 0 : _b.call(_a, context);
            if (!qe) {
                setAdvancedError(context.$L('uxp-core.text.query-engine-unavailable'));
                return;
            }
            // Execute once, validate the output fields against the view's columns before
            // committing — a query that returns none of them can't be selected.
            const execResult = yield qe.executeQuery(pipeline);
            const rows = ((_c = execResult === null || execResult === void 0 ? void 0 : execResult.data) === null || _c === void 0 ? void 0 : _c.result) || [];
            const check = validateOutputFields(rows.length ? Object.keys(rows[0]) : []);
            if ((check === null || check === void 0 ? void 0 : check.status) === 'none') {
                yield alerts.error(context.$L('uxp-core.text.predefined-query-fields-none', { name: q.Name }));
                return;
            }
            const hash = hashPipeline(pipeline);
            setAdvancedQuestion('');
            setAdvancedPipeline(pipeline);
            setOriginalPipeline(pipeline);
            setPipelineHash(hash);
            setOriginalPipelineHash(hash);
            setPipelineRefined(false);
            setPredefinedQuery({ id: q.ID, name: q.Name });
            setPredefinedUnavailable(null);
            setAdvancedData(rows);
            setExtraColumns(discoverExtraColumns(rows));
            setFieldValidation(check);
            setPage(1);
            hasFiltersChangedRef.current = true;
        }
        catch (e) {
            setAdvancedError(context.$L('uxp-core.text.advanced-filter-no-results'));
            console.error('Pick predefined query error:', e);
        }
        finally {
            setAdvancedLoading(false);
        }
    }), [context, validateOutputFields, discoverExtraColumns]);
    // Apply a saved/created query pipeline as the active, unmodified pipeline.
    const applyQueryPipeline = useCallback((pipeline, marker) => {
        const hash = hashPipeline(pipeline);
        setAdvancedPipeline(pipeline);
        setOriginalPipeline(pipeline);
        setPipelineHash(hash);
        setOriginalPipelineHash(hash);
        setPipelineRefined(false);
        setPredefinedQuery(marker);
        setPredefinedUnavailable(null);
        hasFiltersChangedRef.current = true;
        runSavedPipeline(pipeline);
    }, [runSavedPipeline]);
    // Open the "Save as new query" form for the edited pipeline (from the refine editor).
    const handleSaveAsQuery = useCallback((pipeline, schema) => {
        setSaveQueryPipeline(pipeline);
        setSaveQuerySchema(schema);
        setShowSaveQueryForm(true);
    }, []);
    // Create the new predefined query from the captured pipeline + form fields.
    const handleSaveAsQuerySubmit = useCallback((data) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        const pipeline = saveQueryPipeline;
        if (!pipeline)
            return;
        const name = String((_a = data === null || data === void 0 ? void 0 : data.name) !== null && _a !== void 0 ? _a : '').trim();
        if (!name) {
            alerts.error(context.$L('uxp-core.text.name-required'));
            return;
        }
        // Duplicate-name check on Save (mirrors the Data Explorer save). Inline error, keep dialog open.
        const dup = yield findQueryByName(context, name);
        if (dup) {
            (_b = saveQueryFormRef.current) === null || _b === void 0 ? void 0 : _b.setErrors({ name: context.$L('uxp-core.text.query-name-duplicate') });
            return;
        }
        try {
            const created = yield createPredefinedQuery(context, {
                name,
                description: String((_c = data === null || data === void 0 ? void 0 : data.description) !== null && _c !== void 0 ? _c : '').trim(),
                objectTypes: objectType ? [objectType] : [],
                pipeline,
                schema: saveQuerySchema,
                question: advancedQuestion,
            });
            if (!created) {
                toast.error(context.$L('uxp-core.text.query-save-failed'));
                return;
            }
            applyQueryPipeline(pipeline, { id: created.id, name: created.name });
            setShowSaveQueryForm(false);
            setShowRefineModal(false);
            refreshPredefinedQueries();
            toast.success(context.$L('uxp-core.text.query-saved'));
        }
        catch (e) {
            toast.error((e === null || e === void 0 ? void 0 : e.message) || context.$L('uxp-core.text.query-save-failed'));
        }
    }), [context, saveQueryPipeline, saveQuerySchema, advancedQuestion, objectType, applyQueryPipeline, refreshPredefinedQueries]);
    const saveQueryFormStructure = useMemo(() => [
        {
            title: '',
            columns: 1,
            fields: [
                { name: 'name', label: context.$L('uxp-core.text.save-query-name'), type: 'text', validate: { required: true } },
                { name: 'description', label: context.$L('uxp-core.text.save-query-description'), type: 'text' },
            ],
        },
    ], [context]);
    // Save the edited pipeline back into the active predefined query (warn + double confirm).
    const handleSaveChangesToQuery = useCallback((pipeline, schema) => __awaiter(void 0, void 0, void 0, function* () {
        if (!(predefinedQuery === null || predefinedQuery === void 0 ? void 0 : predefinedQuery.id) || !pipeline)
            return;
        const warned = yield alerts.confirm({
            title: context.$L('uxp-core.text.save-changes-warn-title'),
            content: context.$L('uxp-core.text.save-changes-warn-content'),
        });
        if (!warned)
            return;
        const confirmed = yield alerts.confirm({
            title: context.$L('uxp-core.text.save-changes-confirm-title'),
            content: context.$L('uxp-core.text.save-changes-confirm-content'),
        });
        if (!confirmed)
            return;
        try {
            yield saveChangesToQuery(context, predefinedQuery.id, pipeline, schema);
            // The query now IS the pipeline — apply it as unmodified and close the editor.
            applyQueryPipeline(pipeline, predefinedQuery);
            setShowRefineModal(false);
            refreshPredefinedQueries();
            toast.success(context.$L('uxp-core.text.query-updated'));
        }
        catch (e) {
            toast.error((e === null || e === void 0 ? void 0 : e.message) || context.$L('uxp-core.text.query-save-failed'));
        }
    }), [context, predefinedQuery, applyQueryPipeline, refreshPredefinedQueries]);
    const oscDetails = useMemo(() => ({
        selectedView: selectedView,
        page: page,
        pageSize: pageSize,
        filters: debouncedFilters,
        sort: debouncedSort,
        query: query,
        selectedColumns: selectedColumns,
        total: total
    }), [selectedView, page, pageSize, debouncedFilters, query, debouncedSort, selectedColumns, total]);
    useEffect(() => {
        if (appendToURL) {
            setViewInstance(key || null);
        }
    }, [key, appendToURL]);
    // Effect to observe container widths for responsive column calculations
    useEffect(() => {
        let timeoutId;
        const observer = new ResizeObserver(([entry]) => {
            const newWidth = Math.round(entry.contentRect.width);
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setContainerWidth(prev => {
                    // Only update if difference is more than 5px
                    if (Math.abs(prev - newWidth) > 5) {
                        return newWidth;
                    }
                    return prev;
                });
            }, 100);
        });
        if (containerRef.current)
            observer.observe(containerRef.current);
        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
        };
    }, []);
    useEffect(() => {
        let timeoutId;
        const observer = new ResizeObserver(([entry]) => {
            const newWidth = Math.round(entry.contentRect.width);
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setListContainerWidth(prev => {
                    // Only update if difference is more than 5px
                    if (Math.abs(prev - newWidth) > 5) {
                        return newWidth;
                    }
                    return prev;
                });
            }, 100);
        });
        if (listContainerRef.current)
            observer.observe(listContainerRef.current);
        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
        };
    }, []);
    const applyQueryAndFilters = useCallback((data, query, filters) => {
        var _a;
        let result = cloneDeep(data);
        if (!result || result.length === 0)
            return [];
        const searchConfig = searchConfigRef.current;
        // query
        if (hasValue(query) && (searchConfig === null || searchConfig === void 0 ? void 0 : searchConfig.enable) && ((_a = searchConfig === null || searchConfig === void 0 ? void 0 : searchConfig.fields) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            result = result.filter(r => (searchConfig.fields || []).some(f => textSearch(r === null || r === void 0 ? void 0 : r[f], query, true)));
        }
        // filters
        if ((filters === null || filters === void 0 ? void 0 : filters.filters) && Object.keys(filters.filters).length > 0) {
            result = result.filter(r => Object.entries(filters.filters).every(([key, value]) => r[key] === value));
        }
        return result;
    }, []);
    const filterDataFromArray = useCallback((_data, _page, _pageSize, _query, _filters, _sort) => {
        const result = sortRows(applyQueryAndFilters(_data, _query, _filters), _sort);
        // pagination
        const start = Math.max((_page - 1) * _pageSize, 0);
        const end = start + _pageSize;
        return result.slice(start, end);
    }, [applyQueryAndFilters]);
    useEffect(() => {
        var _a;
        if (filterMode === 'advanced') {
            setTotal((_a = advancedData === null || advancedData === void 0 ? void 0 : advancedData.length) !== null && _a !== void 0 ? _a : 0);
            return;
        }
        if (typeof propTotal === 'function') {
            propTotal(query, debouncedFilters).then(setTotal).catch(console.error);
        }
        else if (Array.isArray(propData)) {
            const filtered = applyQueryAndFilters(propData, query, debouncedFilters);
            setTotal(filtered.length);
        }
        else if (typeof propTotal === 'number') {
            setTotal(propTotal);
        }
    }, [propTotal, propData, query, debouncedFilters, applyQueryAndFilters, filterMode, advancedData]);
    // Fallback total: when the caller supplies no explicit total (the `total` prop is not a
    // function and not a positive number), derive it from the backend's per-row __totalcount__
    // (SDModel __includetotalcount__) that rides with the page. Keyed off the prop (not the
    // `total` state) so it re-derives on every data change and stays correct after a filter /
    // search / page change instead of freezing at the first page's count (LPVR-824).
    useEffect(() => {
        var _a;
        if (filterMode === 'advanced')
            return;
        if (Array.isArray(propData))
            return; // static array: primary effect owns the total
        if (typeof propTotal === 'function')
            return; // explicit async count owns the total
        if (typeof propTotal === 'number' && propTotal > 0)
            return; // explicit numeric total
        setTotal(toNum((_a = data === null || data === void 0 ? void 0 : data[0]) === null || _a === void 0 ? void 0 : _a['__totalcount__'], 0));
    }, [data, propData, propTotal, filterMode]);
    const loadData = useCallback((...args_1) => __awaiter(void 0, [...args_1], void 0, function* (silent = false) {
        if (!hasViewSelected)
            return;
        if (filterMode === 'advanced') {
            if (!advancedData)
                return;
            if (isInfinite) {
                // Infinite: everything up to this page
                setData(advancedData.slice(0, page * pageSize));
                infinitePaging.setLastBatch(Math.max(0, Math.min(pageSize, advancedData.length - (page - 1) * pageSize)));
                return;
            }
            const start = (page - 1) * pageSize;
            setData(advancedData.slice(start, start + pageSize));
            return;
        }
        if (isInfinite) {
            // Infinite: page 1 replaces the list, later pages add to it
            yield infinitePaging.load(page, () => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                return typeof propData === 'function'
                    ? ((_a = (yield propData(page, pageSize, query, debouncedFilters, debouncedSort))) === null || _a === void 0 ? void 0 : _a.items) || []
                    : filterDataFromArray(propData || [], page, pageSize, query, debouncedFilters, debouncedSort);
            }), silent);
            return;
        }
        if (!silent)
            setLoading(true);
        try {
            if (typeof propData === 'function') {
                const res = yield propData(page, pageSize, query, debouncedFilters, debouncedSort);
                setData((res === null || res === void 0 ? void 0 : res.items) || []);
            }
            else {
                const data = filterDataFromArray(propData || [], page, pageSize, query, debouncedFilters, debouncedSort);
                setData([...data]);
            }
        }
        catch (e) {
            toast.error('Unable to get data. Something went wrong.');
            console.error('Unable to get data:', e);
        }
        finally {
            if (!silent)
                setLoading(false);
        }
    }), [page, pageSize, propData, query, debouncedFilters, debouncedSort, filterDataFromArray, hasViewSelected, filterMode, advancedData, isInfinite, infinitePaging.setLastBatch, infinitePaging.load]);
    // Infinite: the table or card grid asks for the next page when the end of the list comes into view
    const hasMore = isInfinite && infinitePaging.hasMore(data.length, total);
    const infiniteResetKey = `${query}|${JSON.stringify(debouncedFilters)}|${JSON.stringify(debouncedSort)}|${displayMode}`;
    const infiniteProps = isInfinite ? {
        hasMore: hasMore && !infinitePaging.busy,
        count: data.length,
        appending,
        error: loadError,
        onLoadMore: () => setPage(p => p + 1),
        onRetry: () => loadData(),
        resetKey: infiniteResetKey,
    } : undefined;
    // Infinite: reload from the top (refresh)
    const reloadFromStart = useCallback((silent) => {
        if (page === 1) {
            loadData(silent);
            return;
        }
        silentReloadRef.current = !!silent;
        setPage(1);
    }, [page, loadData]);
    useEffect(() => {
        const silent = silentReloadRef.current;
        silentReloadRef.current = false;
        loadData(silent);
    }, [loadData]);
    // Manual refresh: reload the current page (or rerun the active query) and its total.
    const canRefresh = props.allowRefresh !== false && (filterMode === 'advanced' ? !!advancedPipeline : typeof propData === 'function');
    // An async total reruns only on search or filter changes, so a reload (e.g. after a delete) asks again
    const refreshTotal = useCallback(() => {
        if (filterMode !== 'advanced' && typeof propTotal === 'function')
            propTotal(query, debouncedFilters).then(setTotal).catch(console.error);
    }, [filterMode, propTotal, query, debouncedFilters]);
    const handleRefresh = useCallback(() => {
        if (filterMode === 'advanced') {
            runSavedPipeline(advancedPipeline);
            return;
        }
        if (isInfinite)
            reloadFromStart();
        else
            loadData();
        refreshTotal();
    }, [filterMode, advancedPipeline, runSavedPipeline, loadData, refreshTotal, isInfinite, reloadFromStart]);
    useImperativeHandle(ref, () => ({
        export: () => exportCurrentView(),
        getDetails: () => oscDetails,
        refreshCurrentPage: () => { refreshTotal(); return isInfinite ? reloadFromStart() : loadData(); },
        silentRefreshCurrentPage: () => { refreshTotal(); return isInfinite ? reloadFromStart(true) : loadData(true); },
        setPageData: (items) => setData(items),
        getCurrentPageData: () => data,
        getFilters: () => filters,
        applyFilters: (f) => {
            if (!f || typeof f !== 'object' || typeof f.filters !== 'object')
                return;
            setFilters(cleanFilters(f));
            setPage(1);
        },
        isFilterPanelOpen: () => showFilterPanel,
    }), [exportCurrentView, oscDetails, loadData, data, filters, showFilterPanel, isInfinite, reloadFromStart, refreshTotal]);
    // Handlers for row and column interactions
    const handleColumnResize = useCallback((id, newWidth) => {
        setProcessedColumns(prev => {
            if (!(prev === null || prev === void 0 ? void 0 : prev[id]))
                return prev;
            const column = prev[id];
            const width = hasValue(column.maxWidth)
                ? Math.min(column.maxWidth, Math.max(newWidth, column.minWidth || 0))
                : Math.max(newWidth, column.minWidth || 0);
            setResizedColumns(prev => (Object.assign(Object.assign({}, prev), { [id]: width })));
            if (width === column.width)
                return prev;
            return Object.assign(Object.assign({}, prev), { [id]: Object.assign(Object.assign({}, column), { width }) });
        });
    }, []);
    const isRowSelected = useCallback((item) => {
        if (selected.some(s => (s === null || s === void 0 ? void 0 : s[idField]) === (item === null || item === void 0 ? void 0 : item[idField])))
            return true;
        if (isDetailsPanelShowing && String(item === null || item === void 0 ? void 0 : item[idField]) === String(viewInstance))
            return true;
        return false;
    }, [selected, idField, isDetailsPanelShowing, viewInstance]);
    const handleRowSelect = useCallback((item) => {
        const updatedSelection = isRowSelected(item)
            ? selected.filter(s => (s === null || s === void 0 ? void 0 : s[idField]) !== (item === null || item === void 0 ? void 0 : item[idField]))
            : [...selected, item];
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(updatedSelection);
    }, [selected, isRowSelected, onSelect, idField]);
    // Phones: the row whose fields show in a sheet (rows with no tap action of their own)
    const [detailRow, setDetailRow] = useState(null);
    const handleRowClick = useCallback((e, item) => {
        if (onClickRow) {
            onClickRow(e, item);
        }
        else if (detailsPanel) {
            if (appendToURL) {
                const extraParams = {};
                if (nameField && item[nameField] != null)
                    extraParams[urlParams.name] = String(item[nameField]);
                if (typeField && item[typeField] != null)
                    extraParams[urlParams.type] = String(item[typeField]);
                navigate(generateURL({ searchParams: Object.assign({ [urlParams.key]: item[idField] }, extraParams), removeParams: clearParamsOnSelect }));
            }
            else {
                setViewInstance(item[idField]);
            }
        }
        else if (isMobile) {
            // No tap action of its own: show all the row's fields (its card shows three)
            setDetailRow(item);
        }
    }, [onClickRow, detailsPanel, appendToURL, urlParams, idField, nameField, typeField, isMobile]);
    const onSelectRows = useCallback((rows) => {
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(rows);
    }, [onSelect]);
    // Effect to construct the list of all possible columns
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        const _allColumns = [];
        if (((_a = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _a === void 0 ? void 0 : _a.enable) && !isDetailsPanelShowing)
            _allColumns.push({ id: '__actionsColumn__', label: 'Actions' });
        if ((_b = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.expandColumn) === null || _b === void 0 ? void 0 : _b.enable)
            _allColumns.push({ id: '__expandColumn__', label: 'Expand' });
        if (((_c = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.selectColumn) === null || _c === void 0 ? void 0 : _c.enable) && !isDetailsPanelShowing)
            _allColumns.push({ id: '__selectColumn__', label: 'Select' });
        _allColumns.push(...(propColumns || []));
        // Append extra columns returned by advanced AI search (de-duplicated against propColumns)
        if (extraColumns.length > 0) {
            const existingIds = new Set(propColumns.map(c => c.id));
            _allColumns.push(...extraColumns.filter(c => !existingIds.has(c.id)));
        }
        if (((_d = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.editColumn) === null || _d === void 0 ? void 0 : _d.enable) && !isDetailsPanelShowing)
            _allColumns.push({ id: '__editColumn__', label: 'Edit' });
        if (((_e = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.deleteColumn) === null || _e === void 0 ? void 0 : _e.enable) && !isDetailsPanelShowing)
            _allColumns.push({ id: '__deleteColumn__', label: 'Delete' });
        setAllColumns(_allColumns);
    }, [propColumns, defaultActionColumns, isDetailsPanelShowing, extraColumns]);
    const sortableColumns = useMemo(() => {
        return ((selectedColumns === null || selectedColumns === void 0 ? void 0 : selectedColumns.length) > 0 ? allColumns.filter(c => selectedColumns.includes(c.id)) : allColumns)
            .filter(c => !c.id.startsWith('__') && c.isSortable);
    }, [allColumns, selectedColumns]);
    const multiColumnSortEnabled = useMemo(() => {
        var _a, _b, _c, _d, _e;
        return (((_a = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _a === void 0 ? void 0 : _a.enable) || false) && (((_c = (_b = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _b === void 0 ? void 0 : _b.headerActions) === null || _c === void 0 ? void 0 : _c.sort) || false) && (((_e = (_d = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _d === void 0 ? void 0 : _d.headerActions) === null || _e === void 0 ? void 0 : _e.multiColumnSort) || false);
    }, [defaultActionColumns]);
    const handleSort = useCallback((value) => {
        setSort(prev => {
            if (typeof value == 'string') {
                const currentState = (prev === null || prev === void 0 ? void 0 : prev[value]) || 0;
                const newState = currentState === 0 ? 1 : currentState === 1 ? -1 : 0;
                return cleanSort(Object.assign(multiColumnSortEnabled ? prev : {}, { [value]: newState }));
            }
            else
                return cleanSort(value); // here from base table sorting is handled
        });
        hasSortChangedRef.current = true;
        setPage(1);
    }, [multiColumnSortEnabled]);
    // Memoize the calculation of default action columns to avoid re-computation
    const memoizedDefaultActionColumns = useMemo(() => {
        var _a, _b, _c, _d, _e;
        const dacs = {};
        if (isDetailsPanelShowing)
            return dacs;
        if ((_a = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.editColumn) === null || _a === void 0 ? void 0 : _a.enable)
            dacs.editColumn = Object.assign(Object.assign({}, defaultActionColumns.editColumn), { width: 60 });
        if ((_b = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.deleteColumn) === null || _b === void 0 ? void 0 : _b.enable)
            dacs.deleteColumn = Object.assign(Object.assign({}, defaultActionColumns.deleteColumn), { width: 60 });
        if ((_c = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.selectColumn) === null || _c === void 0 ? void 0 : _c.enable)
            dacs.selectColumn = Object.assign(Object.assign({}, defaultActionColumns.selectColumn), { width: 40, onSelectAll: (selected) => onSelectRows(selected ? data : []) });
        if ((_d = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.expandColumn) === null || _d === void 0 ? void 0 : _d.enable)
            dacs.expandColumn = Object.assign(Object.assign({}, defaultActionColumns.expandColumn), { width: 40 });
        if ((_e = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _e === void 0 ? void 0 : _e.enable) {
            const headerActions = [];
            const { customiseColumns, clipOverflowText, sort: enableSort } = defaultActionColumns.actionsColumn.headerActions || {};
            if (customiseColumns) {
                headerActions.push({
                    label: context.$L('uxp-core.text.customise-columns'),
                    icon: 'fal columns',
                    children: () => (_jsx("div", { className: "uxpcore_actions_list", children: allColumns.filter(c => !c.id.startsWith('__') && hasValue(c.label)).map(col => (_jsx("div", { className: "uxpcore_actions_list__action", children: _jsx(Checkbox, { checked: selectedColumns.includes(col.id), label: col.label, type: "bordered", onChange: checked => {
                                    setSelectedColumns(prev => checked ? [...prev, col.id] : prev.filter(id => id !== col.id));
                                    hasColumnsChangedRef.current = true;
                                } }) }, col.id))) })),
                    ref: columnsDropdwonRef
                });
            }
            if (clipOverflowText) {
                headerActions.push({
                    label: context.$L('uxp-core.text.clip-overflowing-text'),
                    icon: 'fal cut',
                    children: () => (_jsx("div", { className: "uxpcore_actions_list", children: "Options goes here" }))
                });
            }
            if (enableSort && (sortableColumns === null || sortableColumns === void 0 ? void 0 : sortableColumns.length) > 0) {
                headerActions.push({
                    label: context.$L('uxp-core.text.sort-by'),
                    icon: 'fal sort',
                    children: () => (_jsx("div", { className: "uxpcore_actions_list", children: (sortableColumns || []).map(col => {
                            const sortDir = (sort === null || sort === void 0 ? void 0 : sort[col.id]) || 0;
                            return (_jsxs("div", { className: classNames("uxpcore_actions_list__action", "uxpcore_table__actions-sortaction", { "uxpcore_table__actions-sortaction--active": sortDir !== 0 }), onClick: () => { handleSort(col.id); }, children: [_jsx("div", { className: "uxpcore_actions_list__action-text", children: col.label }), sortDir !== 0 && (_jsx("div", { className: "uxpcore_actions_list__action-icon", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', sortDir === 1 ? 'caret-up' : 'caret-down'] }) }))] }, col.id));
                        }) })),
                    ref: sortDropdwonRef
                });
            }
            dacs.actionsColumn = Object.assign(Object.assign({}, defaultActionColumns.actionsColumn), { width: 40, headerActions, ref: actionsDropdwonRef });
        }
        return dacs;
    }, [defaultActionColumns, isDetailsPanelShowing, onSelectRows, data, context, allColumns, selectedColumns, sort, sortableColumns, handleSort]);
    // Memoize the selection of active columns based on view/panel state
    // Phones without a compact column from the app: rows show as cards built from the columns
    const autoCard = isMobile && !cardColumn && !((_c = detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columns) === null || _c === void 0 ? void 0 : _c.length);
    // The all-fields sheet (a tap on a row with no action) also lists the row's actions
    const detailRowActions = useMemo(() => rowActionsOf(defaultActionColumns), [defaultActionColumns]);
    // The columns an automatic phone card is built from (and the all-fields sheet shows)
    const cardSourceColumns = useMemo(() => {
        let columns = [...propColumns, ...extraColumns];
        if (selectedColumns.length > 0) {
            const selectedSet = new Set(selectedColumns);
            columns = columns.filter(c => selectedSet.has(c.id));
        }
        const hidden = new Set((detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columnsToHide) || []);
        return columns.filter(c => !hidden.has(c.id));
    }, [detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columnsToHide, selectedColumns, propColumns, extraColumns]);
    const activeColumns = useMemo(() => {
        var _a, _b;
        if (useCompactColumns && cardColumn)
            return [cardColumn];
        const showDetailsPanelColumns = useCompactColumns && !!((_a = detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columns) === null || _a === void 0 ? void 0 : _a.length);
        if (showDetailsPanelColumns) {
            return detailsPanel.columns;
        }
        // Details panel open and no compact column from the page: one list column. With a card layout,
        // a list row from it (picture, title, description); otherwise the phone's card column.
        if (useCompactColumns && !((_b = detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columns) === null || _b === void 0 ? void 0 : _b.length)) {
            if (card)
                return [{
                        id: '__cardrow__',
                        label: '',
                        renderColumn: (item) => {
                            const image = pickCardField(item, card.image);
                            return _jsx(ListItemCard, { title: pickCardField(item, card.title), subtitle: pickCardField(item, card.description) || undefined, image: typeof image === 'string' ? image : undefined });
                        },
                    }];
            return [buildCardColumn(cardSourceColumns)];
        }
        let columns = [...propColumns, ...extraColumns];
        if (selectedColumns.length > 0) {
            const selectedSet = new Set(selectedColumns);
            columns = columns.filter(c => selectedSet.has(c.id));
        }
        if (autoCard)
            return [buildCardColumn(cardSourceColumns)];
        return columns;
    }, [useCompactColumns, autoCard, isMobile, cardColumn, card, detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columns, selectedColumns, propColumns, extraColumns, cardSourceColumns]);
    // Effect to calculate final column set
    useEffect(() => {
        if (listContainerWidth <= 0)
            return;
        let dacs = memoizedDefaultActionColumns;
        if (selectedColumns.length > 0) {
            const selectedSet = new Set(selectedColumns);
            dacs = Object.entries(memoizedDefaultActionColumns).reduce((acc, [key, value]) => {
                if (key === 'actionsColumn' || selectedSet.has(`__${key}__`)) {
                    acc[key] = value;
                }
                return acc;
            }, {});
        }
        const columnsToHide = useCompactColumns ? (detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columnsToHide) || [] : [];
        const processed = processColumns({
            context,
            columns: activeColumns,
            defaultColumns: dacs,
            containerWidth: listContainerWidth,
            minCellWidth,
            columnsToHide: selectedColumns.length > 0 ? [] : columnsToHide,
            selectedCount: selected.length,
            totalCount: data.length,
            resizedColumns
        });
        setProcessedColumns(processed);
    }, [context, listContainerWidth, activeColumns, memoizedDefaultActionColumns, useCompactColumns, detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columnsToHide, minCellWidth, selected.length, data.length, selectedColumns, resizedColumns]);
    // Memoize panel widths
    const widths = useMemo(() => {
        if (!detailsPanel || !isDetailsPanelShowing)
            return { list: '100%', details: '0%' };
        if (isMobile)
            return { list: '0%', details: '100%' };
        let listWidth = collapsedWidth;
        const rangeRegex = /^(1[5-9]|[2-6][0-9]|7[0-5])%$/;
        if (typeof listWidth === 'number' && containerWidth > 0) {
            listWidth = `${(listWidth / containerWidth) * 100}%`;
        }
        let listPercent = parseFloat(String(listWidth));
        if (!rangeRegex.test(String(listWidth).trim())) {
            listPercent = Math.min(75, Math.max(15, listPercent));
        }
        const detailsPercent = 100 - listPercent;
        return {
            list: `${listPercent}%`,
            details: `${detailsPercent}%`
        };
    }, [collapsedWidth, containerWidth, detailsPanel, isDetailsPanelShowing, isMobile]);
    // Views and Filters Logic
    const defaultViews = useMemo(() => (views === null || views === void 0 ? void 0 : views.defaultViews) || [], [views === null || views === void 0 ? void 0 : views.defaultViews]);
    const onViewChange = useCallback((view) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        setViewAction(null);
        setShowViewsList(false);
        setSelectedView(view);
        setViewName((view === null || view === void 0 ? void 0 : view.name) || '');
        setPageSize(((_a = view === null || view === void 0 ? void 0 : view.configurations) === null || _a === void 0 ? void 0 : _a.pageSize) || initialPageSize);
        setPage(1); // a view carries its own filters/sort — start from the first page
        setDisplayMode(((_b = view === null || view === void 0 ? void 0 : view.configurations) === null || _b === void 0 ? void 0 : _b.display) || defaultDisplay);
        const savedMode = ((_c = view === null || view === void 0 ? void 0 : view.configurations) === null || _c === void 0 ? void 0 : _c.filterMode) || 'simple';
        setFilterMode(savedMode);
        if (savedMode === 'advanced' && ((_d = view === null || view === void 0 ? void 0 : view.configurations) === null || _d === void 0 ? void 0 : _d.advancedFilter)) {
            const af = view.configurations.advancedFilter;
            const { question, pipeline, pipelineHash: savedPipelineHash, originalPipeline: savedOriginal, originalPipelineHash: savedHash, isRefined, isPredefined, predefinedQueryId, predefinedQueryName } = af;
            setAdvancedQuestion(question || '');
            // Clear simple filter state
            setFilters({ filters: {} });
            immediateUpdateFilters({ filters: {} });
            setSelectedColumns([...(((_e = view === null || view === void 0 ? void 0 : view.configurations) === null || _e === void 0 ? void 0 : _e.columns) || [])]);
            setSort({});
            immediateUpdateSort({});
            if (isPredefined && predefinedQueryId) {
                // Reference-based: resolve the query's current pipeline live (the "original").
                setPredefinedQuery({ id: predefinedQueryId, name: predefinedQueryName !== null && predefinedQueryName !== void 0 ? predefinedQueryName : '' });
                resolvePredefinedQueryById(context, predefinedQueryId).then(resolved => {
                    var _a;
                    const queryPipeline = (_a = resolved === null || resolved === void 0 ? void 0 : resolved.pipeline) !== null && _a !== void 0 ? _a : null;
                    if (queryPipeline) {
                        setPredefinedUnavailable(null);
                        // Use the query's LIVE name, not the stored (possibly renamed) one.
                        setPredefinedQuery({ id: predefinedQueryId, name: resolved.name || (predefinedQueryName !== null && predefinedQueryName !== void 0 ? predefinedQueryName : '') });
                        const origHash = hashPipeline(queryPipeline);
                        setOriginalPipeline(queryPipeline);
                        setOriginalPipelineHash(origHash);
                        if (isRefined && pipeline) {
                            setAdvancedPipeline(pipeline);
                            setPipelineHash(savedPipelineHash !== null && savedPipelineHash !== void 0 ? savedPipelineHash : hashPipeline(pipeline));
                            setPipelineRefined(true);
                            runSavedPipeline(pipeline);
                        }
                        else {
                            setAdvancedPipeline(queryPipeline);
                            setPipelineHash(origHash);
                            setPipelineRefined(false);
                            runSavedPipeline(queryPipeline);
                        }
                    }
                    else if (pipeline) {
                        // Query no longer available — keep the stored (modified) pipeline usable, but flag it.
                        setPredefinedUnavailable({ name: predefinedQueryName });
                        const h = hashPipeline(pipeline);
                        setAdvancedPipeline(pipeline);
                        setPipelineHash(h);
                        setOriginalPipeline(pipeline);
                        setOriginalPipelineHash(h);
                        setPipelineRefined(!!isRefined);
                        runSavedPipeline(pipeline);
                    }
                    else {
                        // Reference-only view whose query is gone — nothing to run; prompt the user.
                        setPredefinedUnavailable({ name: predefinedQueryName });
                        setAdvancedPipeline(null);
                        setAdvancedData(null);
                        setExtraColumns([]);
                        setFieldValidation(null);
                    }
                });
            }
            else {
                // Non-predefined: restore the stored pipeline + original.
                const fallbackHash = hashPipeline(pipeline);
                setPredefinedQuery(null);
                setPredefinedUnavailable(null);
                setAdvancedPipeline(pipeline);
                setPipelineHash(savedPipelineHash !== null && savedPipelineHash !== void 0 ? savedPipelineHash : fallbackHash);
                setOriginalPipeline(savedOriginal !== null && savedOriginal !== void 0 ? savedOriginal : pipeline);
                setOriginalPipelineHash(savedHash !== null && savedHash !== void 0 ? savedHash : fallbackHash);
                setPipelineRefined(!!isRefined);
                runSavedPipeline(pipeline);
            }
        }
        else {
            // Restore simple filter state
            setAdvancedData(null);
            setAdvancedPipeline(null);
            setPipelineHash(null);
            setOriginalPipeline(null);
            setOriginalPipelineHash(null);
            setAdvancedQuestion('');
            setPipelineRefined(false);
            setExtraColumns([]);
            setFieldValidation(null);
            const newFilters = cleanFilters(Object.assign({}, (((_f = view === null || view === void 0 ? void 0 : view.configurations) === null || _f === void 0 ? void 0 : _f.filters) || { filters: {} })));
            const newSort = cleanSort(Object.assign({}, (((_g = view === null || view === void 0 ? void 0 : view.configurations) === null || _g === void 0 ? void 0 : _g.sort) || {})));
            setFilters(newFilters);
            immediateUpdateFilters(newFilters);
            setSelectedColumns([...(((_h = view === null || view === void 0 ? void 0 : view.configurations) === null || _h === void 0 ? void 0 : _h.columns) || [])]);
            setSort(newSort);
            immediateUpdateSort(newSort);
        }
    }, [initialPageSize, immediateUpdateFilters, immediateUpdateSort, runSavedPipeline, context, defaultDisplay]);
    const addViewsProps = useMemo(() => ({
        enable: views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews,
        onTriggerAdd: () => {
            setShowViewsList(false);
            setViewAction('add');
        },
    }), [views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews]);
    const editViewsProps = useMemo(() => ({
        enable: views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews,
        onTriggerEdit: () => {
            setShowViewsList(false);
            setViewAction('edit');
        },
    }), [views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews]);
    const deleteViewsProps = useMemo(() => ({
        enable: views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews,
    }), [views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews]);
    const renderTitle = useCallback(() => {
        var _a;
        const hasFilters = !!filterConfig && (!!((_a = filterConfig.formFields) === null || _a === void 0 ? void 0 : _a.length) || !!filterConfig.renderFilters || !!filterConfig.advanced);
        const hasTitle = !!props.title;
        return (_jsxs("div", { className: "uxpcore_osc__title_container", children: [hasFilters && (_jsx(FilterView, { asSheet: isMobile, isOpen: showFilterPanel, onToggle: () => setShowFilterPanel(p => !p), appliedFilters: filters, onChange: (f) => {
                        hasFiltersChangedRef.current = true;
                        setFilters(cleanFilters(f));
                        setPage(1);
                    }, formFields: filterConfig.formFields || [], renderCustom: filterConfig.renderFilters, getFilterCount: filterConfig.getFilterCount, filterMode: filterMode, onFilterModeChange: (mode) => {
                        setFilterMode(mode);
                        if (mode === 'simple') {
                            setAdvancedData(null);
                            setExtraColumns([]);
                            hasFiltersChangedRef.current = false;
                        }
                        else {
                            setFilters({ filters: {} });
                            immediateUpdateFilters({ filters: {} });
                            // Re-execute the pipeline if one was active
                            if (advancedPipeline) {
                                runSavedPipeline(advancedPipeline);
                            }
                        }
                    }, advancedConfig: filterConfig.advanced, advancedQuestion: advancedQuestion, onAdvancedQuestionChange: (q) => { setAdvancedQuestion(q); setAdvancedError(null); }, onAdvancedSubmit: () => runAdvancedSearch(advancedQuestion), advancedLoading: advancedLoading, hasActivePipeline: !!advancedPipeline, advancedError: advancedError, activePipeline: advancedPipeline, onEditRefinement: () => setShowRefineModal(true), pipelineRefined: pipelineRefined, hasOriginalPipeline: !!originalPipeline && pipelineRefined, onRestoreOriginalPipeline: handleRestoreOriginal, predefinedQueries: predefinedQueries, predefinedLoading: predefinedLoading, onPickPredefined: handlePickPredefined, selectedPredefinedId: predefinedQuery === null || predefinedQuery === void 0 ? void 0 : predefinedQuery.id, predefinedQueryName: predefinedQuery === null || predefinedQuery === void 0 ? void 0 : predefinedQuery.name, predefinedUnavailable: predefinedUnavailable })), (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.advanced) && (_jsx(PipelineRefineModal, { show: showRefineModal, pipeline: advancedPipeline, onClose: () => setShowRefineModal(false), onApply: handleRefinementApplied, isPredefined: !!predefinedQuery, onSaveAsQuery: handleSaveAsQuery, onSaveChangesToQuery: handleSaveChangesToQuery })), (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.advanced) && (_jsx(SlideInForm, { ref: saveQueryFormRef, isOpen: showSaveQueryForm, title: context.$L('uxp-core.text.save-query-title'), formType: "dynamic", formStructure: saveQueryFormStructure, onSave: handleSaveAsQuerySubmit, onCancel: () => setShowSaveQueryForm(false), mode: "modal", hideInstructions: true })), hasViews
                    ? (_jsx(ViewsList, { isOpen: showViewsList, onToggle: () => setShowViewsList(p => !p), listId: views.listId, defaultViews: defaultViews, selectedViewId: selectedView === null || selectedView === void 0 ? void 0 : selectedView.id, onChangeView: onViewChange, addViews: addViewsProps, editViews: editViewsProps, deleteViews: deleteViewsProps, ref: viewsListRef, defaultViewId: views === null || views === void 0 ? void 0 : views.defaultviewId, onViewsLoaded: setLoadedViews }))
                    : (_jsx("div", { className: "uxpcore_osc__title", children: hasTitle ? props.title : '' }))] }));
    }, [isMobile, props.title, filterConfig, views, filters, showFilterPanel, selectedView, showViewsList, defaultViews, onViewChange, addViewsProps, hasViews, filterMode, advancedQuestion, advancedLoading, advancedPipeline, advancedError, showRefineModal, handleRefinementApplied, handleRestoreOriginal, runAdvancedSearch, runSavedPipeline, immediateUpdateFilters, pipelineRefined, originalPipeline]);
    const renderButton = (label, count, isActive, onClick) => {
        return _jsx("div", { className: classNames("uxpcore_osc__filters_container-button", {
                "uxpcore_osc__filters_container-button--active": isActive
            }), onClick: onClick, children: _jsx(Badge, { count: count, max: 9, position: 'right', children: label }) });
    };
    const resetChanges = useCallback(() => {
        hasFiltersChangedRef.current = false;
        hasSortChangedRef.current = false;
        hasColumnsChangedRef.current = false;
        setViewAction(null);
    }, []);
    const resetView = useCallback(() => {
        var _a, _b, _c, _d, _e, _f, _g;
        setFilters(cleanFilters(((_a = selectedView === null || selectedView === void 0 ? void 0 : selectedView.configurations) === null || _a === void 0 ? void 0 : _a.filters) || { filters: {} }));
        setPage(1);
        setSort(cleanSort(((_b = selectedView === null || selectedView === void 0 ? void 0 : selectedView.configurations) === null || _b === void 0 ? void 0 : _b.sort) || {}));
        setSelectedColumns(((_c = selectedView === null || selectedView === void 0 ? void 0 : selectedView.configurations) === null || _c === void 0 ? void 0 : _c.columns) || []);
        setDisplayMode(((_d = selectedView === null || selectedView === void 0 ? void 0 : selectedView.configurations) === null || _d === void 0 ? void 0 : _d.display) || defaultDisplay);
        setViewName((selectedView === null || selectedView === void 0 ? void 0 : selectedView.name) || '');
        // Clear advanced search state on cancel
        setFilterMode(((_e = selectedView === null || selectedView === void 0 ? void 0 : selectedView.configurations) === null || _e === void 0 ? void 0 : _e.filterMode) || 'simple');
        setAdvancedQuestion(((_g = (_f = selectedView === null || selectedView === void 0 ? void 0 : selectedView.configurations) === null || _f === void 0 ? void 0 : _f.advancedFilter) === null || _g === void 0 ? void 0 : _g.question) || '');
        setAdvancedPipeline(null);
        setAdvancedData(null);
        setExtraColumns([]);
        setOriginalPipelineHash(null);
        setShowRefineModal(false);
        setPipelineRefined(false);
        resetChanges();
    }, [selectedView, resetChanges, defaultDisplay]);
    const saveView = useCallback((forceUpdate, forceNew) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const _view_name = (viewName || '').trim();
        if (!hasValue(_view_name)) {
            alerts.error('Name is required');
            return;
        }
        // forceUpdate=true  → update existing (Update View button)
        // forceNew=true     → create new (Save as New View button — no viewAction state change)
        // viewAction==='add'→ create new (from New Custom View in dropdown)
        // viewAction==='edit'→ update existing (fixes bug where this was creating new)
        const shouldCreateNew = forceNew || (!forceUpdate && viewAction === 'add');
        // Duplicate name check when creating new
        if (shouldCreateNew) {
            const existingNames = (((_a = viewsListRef.current) === null || _a === void 0 ? void 0 : _a.getViews()) || []).map(v => v.name.trim().toLowerCase());
            if (existingNames.includes(_view_name.toLowerCase())) {
                alerts.error(context.$L('uxp-core.text.view-name-duplicate'));
                return;
            }
        }
        const extraConfigs = { filterMode };
        if (canToggleDisplay)
            extraConfigs.display = displayMode;
        if (filterMode === 'advanced' && advancedPipeline) {
            const fallbackHash = hashPipeline(advancedPipeline);
            if (predefinedQuery && !pipelineRefined) {
                // Reference-only: the view points at the query; its pipeline is fetched live on load.
                extraConfigs.advancedFilter = {
                    isPredefined: true,
                    predefinedQueryId: predefinedQuery.id,
                    predefinedQueryName: predefinedQuery.name,
                    isRefined: false,
                };
            }
            else if (predefinedQuery && pipelineRefined) {
                // Modified predefined: store the edited pipeline + the reference (original fetched by id).
                extraConfigs.advancedFilter = {
                    question: advancedQuestion,
                    pipeline: advancedPipeline,
                    pipelineHash: pipelineHash !== null && pipelineHash !== void 0 ? pipelineHash : fallbackHash,
                    isRefined: true,
                    isPredefined: true,
                    predefinedQueryId: predefinedQuery.id,
                    predefinedQueryName: predefinedQuery.name,
                };
            }
            else {
                // Non-predefined (AI/manual): store the full pipeline + original.
                extraConfigs.advancedFilter = {
                    question: advancedQuestion,
                    pipeline: advancedPipeline,
                    pipelineHash: pipelineHash !== null && pipelineHash !== void 0 ? pipelineHash : fallbackHash,
                    originalPipeline: originalPipeline !== null && originalPipeline !== void 0 ? originalPipeline : advancedPipeline,
                    originalPipelineHash: originalPipelineHash !== null && originalPipelineHash !== void 0 ? originalPipelineHash : fallbackHash,
                    isRefined: pipelineRefined,
                };
            }
        }
        (_b = viewsListRef.current) === null || _b === void 0 ? void 0 : _b.save(shouldCreateNew, _view_name, filters, sort, selectedColumns, pageSize, extraConfigs).then(res => {
            if (res) {
                resetChanges();
            }
        });
    }), [viewName, filters, sort, selectedColumns, pageSize, viewAction, filterMode, advancedQuestion, advancedPipeline, pipelineHash, originalPipeline, originalPipelineHash, pipelineRefined, predefinedQuery, resetChanges, context, canToggleDisplay, displayMode]);
    const renderViewDetails = useCallback(() => {
        var _a, _b, _c, _d, _e, _f;
        const userFacingKeys = new Set(((filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.formFields) || []).flatMap(s => (s.fields || []).map((f) => f.name)));
        // dev-provided counter wins; else count formFields keys; fully-custom renders
        // have no formFields — count all applied keys instead of none
        const filtersCount = filterMode === 'advanced'
            ? (advancedPipeline ? 1 : 0)
            : (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.getFilterCount)
                ? filterConfig.getFilterCount(filters)
                : getFilterCount(filters, userFacingKeys.size > 0 ? userFacingKeys : undefined);
        const sortCount = Object.entries(sort).reduce((count, [, value]) => (count += value !== 0 ? 1 : 0), 0);
        const isActionsColumnsEnabled = ((_a = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _a === void 0 ? void 0 : _a.enable) || false;
        const canShowSortOption = isActionsColumnsEnabled && (((_c = (_b = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _b === void 0 ? void 0 : _b.headerActions) === null || _c === void 0 ? void 0 : _c.sort) || false) && ((_d = Object.values(processedColumns || {})) === null || _d === void 0 ? void 0 : _d.some(pc => pc.isSortable));
        const canShowColumnCustomiseOption = ((_f = (_e = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _e === void 0 ? void 0 : _e.headerActions) === null || _f === void 0 ? void 0 : _f.customiseColumns) || false;
        const hasAnyChanges = hasFiltersChangedRef.current || hasSortChangedRef.current || hasColumnsChangedRef.current;
        const showBar = !isDetailsPanelShowing && (addViewsProps === null || addViewsProps === void 0 ? void 0 : addViewsProps.enable) && (!!viewAction || hasAnyChanges);
        if (!showBar)
            return null;
        return _jsxs("div", { className: 'uxpcore_osc__filters_container', children: [_jsx("div", { className: 'uxpcore_osc__filters_container-input', children: _jsx(Input, { value: viewName || '', onChange: setViewName, placeholder: context.$L('uxp-core.text.enter-view-name') }) }), renderButton(context.$L('uxp-core.text.filters'), filtersCount, hasFiltersChangedRef.current, () => { setShowFilterPanel(true); }), canShowSortOption
                    ? renderButton(context.$L('uxp-core.text.sort-by'), sortCount, hasSortChangedRef.current, () => {
                        var _a;
                        (_a = actionsDropdwonRef.current) === null || _a === void 0 ? void 0 : _a.open();
                        setTimeout(() => { var _a; (_a = sortDropdwonRef.current) === null || _a === void 0 ? void 0 : _a.open(); }, 300);
                    })
                    : null, canShowColumnCustomiseOption
                    ? renderButton(context.$L('uxp-core.text.columns'), selectedColumns.length, hasColumnsChangedRef.current, () => {
                        var _a;
                        (_a = actionsDropdwonRef.current) === null || _a === void 0 ? void 0 : _a.open();
                        setTimeout(() => { var _a; (_a = columnsDropdwonRef.current) === null || _a === void 0 ? void 0 : _a.open(); }, 300);
                    })
                    : null, _jsxs("div", { className: 'uxpcore_osc__filters_container-buttons', children: [_jsx(Button, { title: context.$L('uxp-core.text.cancel'), onClick: resetView, leftIcon: 'fal times', variant: 'secondary' }), viewAction === 'add' && (_jsx(Button, { title: context.$L('uxp-core.text.save-view'), onClick: () => saveView(), leftIcon: 'fal save', variant: 'primary' })), viewAction === 'edit' && (_jsxs(_Fragment, { children: [_jsx(Button, { title: context.$L('uxp-core.text.save-view'), onClick: () => saveView(), leftIcon: 'fal save', variant: 'primary' }), _jsx(Button, { title: context.$L('uxp-core.text.save-as-new-view'), onClick: () => saveView(false, true), leftIcon: 'fal plus', variant: 'secondary' })] })), !viewAction && hasAnyChanges && (_jsxs(_Fragment, { children: [(selectedView === null || selectedView === void 0 ? void 0 : selectedView.isCustom) && (_jsx(Button, { title: context.$L('uxp-core.text.update-view'), onClick: () => saveView(true), leftIcon: 'fal save', variant: 'primary' })), _jsx(Button, { title: context.$L('uxp-core.text.save-as-new-view'), onClick: () => saveView(false, true), leftIcon: 'fal plus', variant: 'secondary' })] }))] })] });
    }, [saveView, resetView, defaultActionColumns, processedColumns, isDetailsPanelShowing, viewAction, viewName, selectedView, filters, sort, selectedColumns, filterMode, advancedPipeline, context, addViewsProps]);
    // Stable across renders so the props bag handed to the (memoized) details view
    // keeps a stable `onClose` identity — otherwise a fresh closure every render
    // defeats PluggableView's memo and re-renders the whole details subtree.
    const handleDetailsClose = useCallback(() => {
        var _a;
        if (appendToURL) {
            const extraRemove = [];
            if (nameField)
                extraRemove.push(urlParams.name);
            if (typeField)
                extraRemove.push(urlParams.type);
            navigate(generateURL({ removeParams: [urlParams.key, ...(urlParams.key === 'objectKey' ? ['key'] : []), 'edit', urlParams.detailsTab, urlParams.additionalDetailsTab, ...extraRemove] }));
        }
        else {
            setViewInstance(null);
        }
        if (detailsPanel && !('renderDetails' in detailsPanel))
            (_a = detailsPanel.onClose) === null || _a === void 0 ? void 0 : _a.call(detailsPanel);
    }, [appendToURL, nameField, typeField, urlParams, navigate, generateURL, detailsPanel]);
    // Phones: the details panel fills the screen; the header shows the object's name and the
    // footer Close bar closes it
    useMobileLayer(isMobile && isDetailsPanelShowing, {
        title: (nameField && searchParams[urlParams.name]) || undefined,
        onClose: handleDetailsClose
    });
    // Stable identities so React.memo(ObjectDetailsPanel) can bail out on unrelated
    // re-renders (e.g. every keystroke in the search box). An inline `data` closure /
    // `urlParams` object here would defeat the memo and re-fetch the panel per keystroke.
    const stableDetailsData = useCallback(() => {
        if (detailsPanel && !('renderDetails' in detailsPanel))
            return detailsPanel.data(viewInstance);
        return Promise.resolve(null);
    }, [detailsPanel, viewInstance]);
    const stableDetailsUrlParams = useMemo(() => ({
        detailsTab: urlParams.detailsTab,
        additionalDetailsTab: urlParams.additionalDetailsTab
    }), [urlParams.detailsTab, urlParams.additionalDetailsTab]);
    const renderDetailsPanel = useCallback(() => {
        if (!detailsPanel || !isDetailsPanelShowing)
            return null;
        const isCustomPanel = 'renderDetails' in detailsPanel;
        const onClose = handleDetailsClose;
        // Phones: the details panel places itself as a full-screen layer; the list is hidden meanwhile
        return (_jsx("div", { className: classNames("uxpcore_osc__details_container", {
                'uxpcore_osc__details_container--open': isDetailsPanelShowing
            }), style: { width: widths.details }, children: isCustomPanel
                ? detailsPanel.renderDetails(viewInstance, onClose)
                : _jsx(ObjectDetailsPanel, Object.assign({}, detailsPanel, { data: stableDetailsData, onClose: onClose, appendToURL: appendToURL, urlParams: stableDetailsUrlParams })) }));
    }, [detailsPanel, isDetailsPanelShowing, viewInstance, widths.details, appendToURL, stableDetailsData, stableDetailsUrlParams, handleDetailsClose]);
    const totalWidth = useMemo(() => {
        if (!processedColumns)
            return '100%';
        return Object.values(processedColumns).reduce((total, { width }) => total + width, 0);
    }, [processedColumns]);
    // Card mode: switching doesn't ask to save the view; a save made for another reason keeps it
    const changeDisplay = useCallback((mode) => setDisplayMode(mode), []);
    const displayToggle = canToggleDisplay ? _jsx(ToggleFilter, { className: "uxpcore_osc__display-toggle", size: "medium", value: displayMode, onChange: (v) => changeDisplay(v), options: [
            { label: context.$L('uxp-core.text.list'), value: 'list', icon: 'fal list', iconOnly: true },
            { label: context.$L('uxp-core.text.cards'), value: 'cards', icon: 'fal grid-2', iconOnly: true },
        ] }) : undefined;
    // Card mode: tick the cards for bulk actions (tables use the select column header)
    const isChecked = useCallback((item) => selected.some(s => (s === null || s === void 0 ? void 0 : s[idField]) === (item === null || item === void 0 ? void 0 : item[idField])), [selected, idField]);
    const toggleCard = useCallback((item) => {
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(isChecked(item) ? selected.filter(s => (s === null || s === void 0 ? void 0 : s[idField]) !== (item === null || item === void 0 ? void 0 : item[idField])) : [...selected, item]);
    }, [isChecked, selected, idField, onSelect]);
    const canSelectCards = isCards && !!((_d = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.selectColumn) === null || _d === void 0 ? void 0 : _d.enable) && !isDetailsPanelShowing;
    const checkedCount = data.filter(isChecked).length;
    const renderSelectAll = () => _jsx(Checkbox, { className: "uxpcore_osc__selectall", checked: checkedCount > 0 ? (checkedCount === data.length ? true : 'intermediate') : false, onChange: (v) => onSelectRows(v ? data : []), type: "bordered", label: context.$L('uxp-core.text.select-all') });
    const detailsRowActions = useMemo(() => isDetailsPanelShowing ? {} : rowActionsOf(defaultActionColumns), [isDetailsPanelShowing, defaultActionColumns]);
    // Phones: paging row and the bottom toolbar (views slider, search, filters, More),
    // or the selection bar while rows are selected
    const renderMobileBottom = () => {
        var _a, _b, _c, _d;
        const hasFilters = !!filterConfig && (!!((_a = filterConfig.formFields) === null || _a === void 0 ? void 0 : _a.length) || !!filterConfig.renderFilters || !!filterConfig.advanced);
        const userFacingKeys = new Set(((filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.formFields) || []).flatMap(s => (s.fields || []).map((f) => f.name)));
        const filterCount = filterMode === 'advanced'
            ? (advancedPipeline ? 1 : 0)
            : (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.getFilterCount)
                ? filterConfig.getFilterCount(filters)
                : getFilterCount(filters, userFacingKeys.size > 0 ? userFacingKeys : undefined);
        // Swipe a view left in the pick list: favourite, and rename / delete for custom views
        const manageViews = !!(views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews);
        const viewActions = (v) => _jsxs(_Fragment, { children: [_jsx(Button, { title: context.$L(v.isFavourite ? 'uxp-core.text.remove-favourite' : 'uxp-core.text.add-favourite'), leftIcon: "phr star", variant: "secondary", onClick: () => { var _a; return (_a = viewsListRef.current) === null || _a === void 0 ? void 0 : _a.toggleFavourite(String(v.id)); } }), manageViews && v.isCustom && _jsx(Button, { title: context.$L('uxp-core.text.rename'), leftIcon: "phr pencil-simple", variant: "secondary", onClick: () => { onViewChange(v); setViewAction('edit'); } }), manageViews && v.isCustom && _jsx(Button, { title: context.$L('uxp-core.text.delete'), leftIcon: "phr trash", variant: "danger", onClick: () => { var _a; return (_a = viewsListRef.current) === null || _a === void 0 ? void 0 : _a.deleteView(v); } })] });
        const viewItems = Object.entries(loadedViews).flatMap(([group, list]) => list.map(v => ({ id: String(v.id), label: v.name, group, actions: viewActions(v) })));
        const actions = [];
        if (hasFilters)
            actions.push({
                id: 'filters', icon: 'phr funnel', label: context.$L('uxp-core.text.filters'),
                active: showFilterPanel, dot: filterCount > 0, onClick: () => setShowFilterPanel(o => !o)
            });
        if (props.hidePaging && canRefresh)
            actions.push({
                id: 'refresh', icon: 'phr arrow-clockwise', label: context.$L('uxp-core.text.refresh'), loading, onClick: handleRefresh
            });
        if (props.actionButtons || canToggleDisplay)
            actions.push({
                id: 'more', icon: 'phr dots-three', label: context.$L('uxp-core.text.more'),
                active: moreOpen, onClick: () => setMoreOpen(o => !o)
            });
        const selecting = !!((_b = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.selectColumn) === null || _b === void 0 ? void 0 : _b.enable) && selected.length > 0;
        // Paging: in the toolbar when there are no views to step through, else its own row above it.
        // The list title is not shown on phones: the app header already shows the page name.
        const paging = !props.hidePaging && _jsx("div", { className: "uxpcore_mobiletoolbar__paging", children: _jsx(PaginationComponent, { total: total, page: page, pageSize: pageSize, dataLength: data.length, onPageChange: setPage, onPageSizeChange: ps => { setPageSize(ps); setPage(1); }, loading: loading, allowPageSizeChange: props.allowPageSizeChange, onRefresh: canRefresh ? handleRefresh : undefined, countOnly: isInfinite }) });
        const pagingInToolbar = viewItems.length === 0;
        // Nothing to show in the toolbar (no views, search, paging or actions): leave it out
        const emptyToolbar = !viewItems.length && !((_c = props.search) === null || _c === void 0 ? void 0 : _c.enable) && !paging && !actions.length && !sectionHost;
        return _jsxs(_Fragment, { children: [(!pagingInToolbar || selecting) && paging, selecting
                    ? _jsxs("div", { className: "uxpcore_mobiletoolbar__container uxpcore_osc__mselectbar", children: [_jsx(IconButton, { icon: "phr x", tooltip: context.$L('uxp-core.text.close'), variant: "secondary", borderless: true, onClick: () => onSelectRows([]) }), _jsx("span", { className: "uxpcore_osc__mselectcount", children: context.$L('uxp-core.text.selected', { count: selected.length }) }), _jsx(Button, { title: context.$L('uxp-core.text.select-all'), onClick: () => onSelectRows(data) }), props.bulkActionButtons && _jsx(IconButton, { icon: "phr dots-three", tooltip: context.$L('uxp-core.text.actions'), variant: "secondary", borderless: true, onClick: () => setBulkOpen(true) })] })
                    : !emptyToolbar && _jsx(MobileToolbar, { items: viewItems, selected: selectedView ? String(selectedView.id) : undefined, onSelect: (id) => {
                            const view = Object.values(loadedViews).flat().find(v => String(v.id) === id);
                            if (view)
                                onViewChange(view);
                        }, pickerTitle: context.$L('uxp-core.text.views'), leading: pagingInToolbar ? paging : undefined, search: ((_d = props.search) === null || _d === void 0 ? void 0 : _d.enable) ? { value: query, onChange: handleSearchChange, placeholder: context.$L('uxp-core.text.search') } : undefined, actions: actions, trailing: sectionHost && _jsx("div", { className: "uxpcore_mobiletoolbar__host", ref: sectionHost }) }), _jsx(MobileSheet, { isOpen: viewAction === 'edit', onClose: resetView, title: context.$L('uxp-core.text.rename'), children: _jsxs("div", { className: "uxpcore_osc__mrename", children: [_jsx(Input, { value: viewName || '', onChange: setViewName, placeholder: context.$L('uxp-core.text.enter-view-name') }), _jsxs("div", { className: "uxpcore_osc__mrename-buttons", children: [_jsx(Button, { title: context.$L('uxp-core.text.cancel'), variant: "secondary", onClick: resetView }), _jsx(Button, { title: context.$L('uxp-core.text.save-view'), onClick: () => saveView() })] })] }) }), _jsx(RowDetailsSheet, { columns: cardSourceColumns, row: detailRow, onClose: () => setDetailRow(null), actions: detailRowActions }), _jsx(MobileSheet, { isOpen: moreOpen, onClose: () => setMoreOpen(false), children: _jsx(ButtonListContext.Provider, { value: moreListMode, children: _jsxs("div", { className: "uxpcore_sheetlist", children: [props.actionButtons, canToggleDisplay && _jsx(Button, { title: context.$L(isCards ? 'uxp-core.text.show-as-list' : 'uxp-core.text.show-as-cards'), leftIcon: isCards ? 'phr list' : 'phr squares-four', variant: "secondary", onClick: () => changeDisplay(isCards ? 'list' : 'cards') })] }) }) }), _jsx(MobileSheet, { isOpen: bulkOpen, onClose: () => setBulkOpen(false), children: _jsx(ButtonListContext.Provider, { value: bulkListMode, children: _jsx("div", { className: "uxpcore_sheetlist", children: props.bulkActionButtons }) }) })] });
    };
    // The search / paging row: left out when nothing is in it
    const showToolbarRow = !!((_e = props === null || props === void 0 ? void 0 : props.search) === null || _e === void 0 ? void 0 : _e.enable) || !props.hidePaging || canRefresh || !!displayToggle
        || (!isCards && !isDetailsPanelShowing && selected.length > 0 && !!props.bulkActionButtons);
    return (_jsx("div", { className: classNames("uxpcore_osc__container", props.className), ref: containerRef, children: _jsxs("div", { className: "uxpcore_osc__content", children: [_jsx("div", { className: classNames("uxpcore_osc__list_container", {
                        'uxpcore_osc__list_container--has-details-panel': isDetailsPanelShowing,
                        'uxpcore_osc__list_container--hidden': isMobile && isDetailsPanelShowing
                    }), style: { width: widths.list }, ref: listContainerRef, children: (props.loading || (!processedColumns && !isCards)) ? (_jsx(ViewSkeleton, {}))
                        : (_jsxs(_Fragment, { children: [(!!props.title || hasViews || !!props.actionButtons || !!filterConfig) && (_jsxs("div", { className: classNames("uxpcore_osc__header", { "uxpcore_osc__header--mobile": isMobile }), children: [renderTitle(), _jsx("div", { className: "uxpcore_osc__action_container", children: !isDetailsPanelShowing && props.actionButtons })] })), !isDetailsPanelShowing && predefinedUnavailable && (_jsx("div", { className: "uxpcore_osc__predefined_unavailable", children: _jsx(NotificationBlock, { message: context.$L('uxp-core.text.predefined-query-unavailable', { name: predefinedUnavailable.name || '' }), variant: 'danger', mode: 'compact', layout: 'bordered' }) })), !isDetailsPanelShowing && filterMode === 'advanced' && fieldValidation && (_jsx("div", { className: "uxpcore_osc__field_validation", children: _jsx(NotificationBlock, { message: context.$L(fieldValidation.status === 'none'
                                            ? 'uxp-core.text.predefined-query-fields-none-banner'
                                            : 'uxp-core.text.predefined-query-fields-partial', { fields: fieldValidation.missing.join(', ') }), variant: fieldValidation.status === 'none' ? 'danger' : 'warning', mode: 'compact', layout: 'bordered' }) })), !isMobile && renderViewDetails(), !isDetailsPanelShowing && props.summaryContent && _jsx(SummaryStrip, { children: props.summaryContent }), !isMobile && showToolbarRow && _jsxs("div", { className: "uxpcore_osc__bulkaction_container", children: [!isCards && !isDetailsPanelShowing && selected.length > 0 ? (_jsx("div", { className: "uxpcore_osc__bulkaction_container-left", children: props.bulkActionButtons })) : _jsx(_Fragment, {}), _jsxs("div", { className: "uxpcore_osc__bulkaction_container-right", children: [((_f = props === null || props === void 0 ? void 0 : props.search) === null || _f === void 0 ? void 0 : _f.enable) && (_jsx("div", { className: "uxpcore_osc__search_container", children: _jsx(DebouncedSearchBox, { onChange: handleSearchChange, collapsed: ((_g = props === null || props === void 0 ? void 0 : props.search) === null || _g === void 0 ? void 0 : _g.collapsed) || false }) })), props.hidePaging
                                                    ? _jsxs("div", { className: "uxpcore_pagination__container", children: [displayToggle, canRefresh && _jsx(PaginationButton, { onClick: handleRefresh, disabled: loading, title: context.$L('uxp-core.text.refresh'), children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'rotate-right'] }) })] })
                                                    : _jsx(PaginationComponent, { total: total, page: page, pageSize: pageSize, dataLength: data.length, onPageChange: setPage, onPageSizeChange: ps => { setPageSize(ps); setPage(1); }, loading: loading, allowPageSizeChange: props.allowPageSizeChange, onRefresh: canRefresh ? handleRefresh : undefined, leading: displayToggle, countOnly: isInfinite })] })] }), !isMobile && canSelectCards && selected.length > 0 && _jsxs("div", { className: "uxpcore_osc__cardbulk", children: [renderSelectAll(), props.bulkActionButtons] }), isCards ? _jsx(CardGrid, { data: data, loading: loading, appending: appending, idField: idField, pageSize: pageSize, card: card, renderCard: renderCard, noItemsMessage: props.noItemsMessage, isChecked: isChecked, onToggleSelect: canSelectCards ? toggleCard : undefined, activeId: isDetailsPanelShowing ? viewInstance : null, onOpen: handleRowClick, actions: detailsRowActions, isMobile: isMobile, rowTitle: nameField ? (row) => row === null || row === void 0 ? void 0 : row[nameField] : undefined, footer: infiniteProps && !loading && _jsx(InfiniteFooter, { paging: infiniteProps }), compact: useCompactColumns }) : _jsx(BaseTableComponent, Object.assign({}, props, { infinite: infiniteProps, loading: loading, columns: processedColumns, data: data, totalWidth: totalWidth, handleResize: handleColumnResize, onClickRow: handleRowClick, onSelectRow: handleRowSelect, isRowSelected: isRowSelected, sort: sort, multiColumnSort: multiColumnSortEnabled, onChangeSort: handleSort, hideHeader: props.hideHeader || isMobile && (autoCard || !!cardColumn || (!!((_h = detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columns) === null || _h === void 0 ? void 0 : _h.length) && selectedColumns.length === 0)), rowTitle: nameField ? (row) => row === null || row === void 0 ? void 0 : row[nameField] : undefined })), isMobile && renderMobileBottom()] })) }), renderDetailsPanel()] }) }));
});
/**
 * @export
 * A component for searching, filtering, and displaying tabular data with support for pagination, views, and details panels.
 *
 * @example
 * ```
 * <ObjectSearchComponent
 *   title="Users"
 *   data={[{ id: '1', name: 'John' }, { id: '2', name: 'Jane' }]}
 *   columns={[{ id: 'name', label: 'Name' }]}
 *   pageSize={10}
 *   total={2}
 *   idField="id"
 * />
 * ```
 *
 * @example
 * ```
 * <ObjectSearchComponent
 *   title="Users"
 *   data={async (page, pageSize, query, filters, sort) => {
 *     return { items: [{ id: '1', name: 'John' }] };
 *   }}
 *   columns={[{ id: 'name', label: 'Name', isSortable: true, exportLabel: 'User Name' }]}
 *   pageSize={10}
 *   total={async () => 100}
 *   idField="id"
 *   filters={{ formFields: [{ name: 'name', label: 'Name', type: 'text' }] }}
 *   views={{ listId: 'users', defaultViews: [{ id: 'default', name: 'Default View' }], allowToMangeCustomViews: true }}
 *   defaultActionColumns={{
 *     selectColumn: { enable: true },
 *     actionsColumn: { enable: true, headerActions: { sort: true, customiseColumns: true } }
 *   }}
 *   search={{ enable: true, fields: ['name'] }}
 *   detailsPanel={{ renderDetails: (id, onClose) => <div>Details for {id}</div> }}
 *   collapsedWidth="40%"
 *   appendToURL={true}
 *   onSelect={(selected) => console.log('Selected rows:', selected)}
 * />
 * ```
 *
 * @category Data Display/Object Search
 */
export const ObjectSearchComponent = memo(ObjectSearchComponentBase);
